/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useMemo } from 'react';
import styled from 'styled-components';
import { isString } from 'lodash-es';
import moment, { Moment } from 'moment';
import {
  DatePicker as DefDatePicker,
  DatePickerProps as DefDatePickerProps,
} from '@mui/x-date-pickers';
import { Input as DefInput } from './Input';

export type DatePickerProps = Omit<
  DefDatePickerProps,
  'value' | 'onChange'
> & {
  value: Value;
  error?: boolean;
  helperText?: string;
  onChange: (UTCDateStr: string | null) => void;
};

const DatePicker = ({
  className,
  value: rawValue,
  error,
  helperText,
  onChange,
  ...props
}: DatePickerProps) => {
  const value = useMemo(() => {
    switch (true) {
      case rawValue instanceof Date:
        return moment(rawValue as Date);
      case moment.isMoment(rawValue):
        return rawValue as Moment;
      case isString(rawValue) && !!rawValue:
        return moment.utc(rawValue as string);
      default:
        return null;
    }
  }, [rawValue]);

  return (
    <DefDatePicker
      value={value}
      enableAccessibleFieldDOMStructure={false}
      slots={{ textField: Input }}
      slotProps={{
        textField: {
          className,
          error,
          helperText,
        },
      }}
      onChange={(date) => {
        if (!date) {
          onChange(null);
          return;
        }

        onChange(moment.utc(date).format());
      }}
      {...props}
    />
  );
};

type Value = Date | Moment | string;

const Input = styled(DefInput)``;

export { DatePicker };
